import type { JSONContent } from '@tiptap/core';
import { useReactFlow } from '@xyflow/react';
import { memo, useEffect } from 'react';
import { TextPrimitive } from './primitive';
import { TextTransform } from './transform';
import { useI18n } from '@/providers/i18n';
import { areNodePropsEqual } from '../are-node-props-equal';

export type TextNodeProps = {
  type: string;
  data: {
    generated?: {
      text: string;
      sources?: Array<{ source: { url: string; title?: string } }>;
    };
    // Optional selected prompt template that will be implicitly prepended
    // to the text when passed to downstream nodes
    promptTemplate?: {
      id: string;
      label: string;
      content: string;
    };
    model?: string;
    updatedAt?: string;
    instructions?: string;
    autoRun?: boolean;

    // Tiptap generated JSON content
    content?: JSONContent;

    // Tiptap text content
    text?: string;

    // Optional source and preview when importing from URL
    sourceUrl?: string;
    preview?: {
      title?: string;
      imageUrl?: string;
    };

    // Persisted UI state: whether the node is in Import Mode
    importMode?: boolean;
    /** 节点视图模式：transform 或 primitive */
    viewMode?: 'transform' | 'primitive';
  };
  id: string;
};

export const TextNode = memo((props: TextNodeProps) => {
  const { t } = useI18n();
  const { updateNodeData } = useReactFlow();
  const existingViewMode = (props.data as any)?.viewMode;
  
  // 提示词节点（primitive）和文本模型节点（transform）是分离的
  // primitive 节点永远保持 primitive，不会自动变成 transform
  // 只有明确设置 viewMode: 'transform' 的节点才会显示为 transform
  const isTransform = existingViewMode === 'transform';
  const Component = isTransform ? TextTransform : TextPrimitive;

  // 当节点是 transform 时，若已有 text 且尚无 generated，则将 text 复制到 generated
  // Perf (Phase 9.4): depend on primitive fields, not whole `props.data`.
  const dataText = (props.data as any)?.text;
  const dataGeneratedText = (props.data as any)?.generated?.text;
  useEffect(() => {
    if (!isTransform) return;
    const hasGenerated = Boolean(dataGeneratedText);
    if (dataText && !hasGenerated) {
      updateNodeData(props.id, {
        generated: { ...((props.data as any)?.generated || {}), text: dataText },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, isTransform, dataText, dataGeneratedText, updateNodeData]);

  const title = isTransform ? t('Text Transform Node') : t('Prompt Node');
  return <Component {...props} title={title} />;
}, areNodePropsEqual);
TextNode.displayName = 'TextNode';
